/**
 * Locked Metric Card Component
 *
 * Displays a blurred preview of a metric with a premium overlay
 * Used for metrics that are only available in premium tier
 */

import { Lock } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

interface LockedMetricCardProps {
  label: string;
  tooltip?: string;
}

export function LockedMetricCard({ label }: LockedMetricCardProps) {
  return (
    <Card className="group relative overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500 cursor-pointer">
      {/* Label - always clear */}
      <CardHeader className="pb-1">
        <CardTitle className="text-xs font-medium text-muted-foreground">{label}</CardTitle>
      </CardHeader>

      {/* Blurred value preview */}
      <CardContent className="pb-3">
        <div className="blur-[5px] pointer-events-none select-none opacity-50">
          <div className="space-y-1">
            <div className="text-lg md:text-xl font-bold text-muted-foreground">$0.000</div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <div className="font-medium">+00.0%</div>
              <span>vs $0,000</span>
            </div>
          </div>
        </div>
      </CardContent>

      {/* Premium overlay - shows on hover */}
      <div className="absolute inset-0 flex flex-col items-center justify-center bg-background/90 backdrop-blur-[5px] opacity-0 group-hover:opacity-100 transition-opacity duration-200">
        <Lock className="h-8 w-8 text-muted-foreground mb-2" />
        <p className="text-sm font-semibold text-foreground">Premium Feature</p>
      </div>
    </Card>
  );
}
